import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
    MessageSquare, Plus, Edit, Trash2, Search, X, Image as ImageIcon, Upload,
} from "lucide-react";
import { getUser } from "../../auth/Auth";
import { deleteRequest, uploadFile } from "../../routes/Routes";
import { loadTestimonials, loadTestimonial } from "../../common/Common"; // ← your helpers

const initialForm = {
    name: "",
    role: "",          // fixed from signed-in user
    date: "",          // yyyy-mm-dd
    message: "",
    file: null,
    imagePreview: "",
};

function TmModal({ open, onClose, onSave, saving, initial, isEditing, fixedRole }) {
    const [form, setForm] = useState(initial || { ...initialForm, role: fixedRole || "" });
    useEffect(() => setForm(initial || { ...initialForm, role: fixedRole || "" }), [initial, fixedRole]);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const onPickFile = (e) => {
        const file = e.target.files?.[0] || null;
        update("file", file);
        if (!file) return update("imagePreview", "");
        const reader = new FileReader();
        reader.onload = (ev) => update("imagePreview", String(ev.target?.result || ""));
        reader.readAsDataURL(file);
    };

    if (!open) return null;

    return (
        <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center" onClick={onClose}>
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white rounded-4 shadow-lg w-100" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
                <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                    <h5 className="mb-0 fw-bold">{isEditing ? "Edit Testimonial" : "Create Testimonial"}</h5>
                    <button className="btn btn-light rounded-circle" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="p-3">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Name</label>
                            <input className="form-control" value={form.name} onChange={(e) => update("name", e.target.value)} required />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Role</label>
                            <input className="form-control" value={fixedRole || form.role} disabled readOnly />
                            <div className="form-text">Role is taken from your account.</div>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Date</label>
                            <input type="date" className="form-control" value={form.date} onChange={(e) => update("date", e.target.value)} required />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Message</label>
                            <textarea className="form-control" rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} required />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label d-flex align-items-center gap-2"><Upload size={16} /> Image (optional)</label>
                            <input type="file" accept="image/*" className="form-control" onChange={onPickFile} />
                            {form.imagePreview ? (
                                <div className="mt-2">
                                    <img src={form.imagePreview} alt="Preview" className="rounded border" style={{ width: 120, height: 120, objectFit: "cover" }} />
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end gap-2 p-3 border-top">
                    <button className="btn btn-light" onClick={onClose}>Cancel</button>
                    <button className="btn gradient-secondary text-white" disabled={saving} onClick={() => onSave({ ...form, role: fixedRole || form.role })}>
                        {saving ? "Saving…" : (isEditing ? "Save changes" : "Create")}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

export default function TestimonialManager() {
    const [me, setMe] = useState(undefined);
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("");
    const [loadingList, setLoadingList] = useState(false);
    const [banner, setBanner] = useState({ type: "", text: "" });

    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);

    useEffect(() => { getUser().then(setMe).catch(() => setMe(null)); }, []);
    const token = me?.token;

    const roleNameFromMe = (() => {
        const rname = me?.role?.name || me?.role_name;
        if (rname) return String(rname);
        const rid = Number(me?.role_id ?? me?.roleId);
        if (rid === 1) return "Admin";
        if (rid === 2) return "Basic";
        return "User";
    })();

    const fetchList = useCallback(async () => {
        if (!token) return;
        setLoadingList(true);
        try {
            const list = await loadTestimonials(token); // ← helper
            setRows(Array.isArray(list) ? list : []);
        } catch {
            setRows([]);
            setBanner({ type: "danger", text: "Failed to load testimonials" });
        } finally { setLoadingList(false); }
    }, [token]);

    useEffect(() => { if (me !== undefined && token) fetchList(); }, [me, token, fetchList]);

    const filtered = rows.filter((r) => {
        const q = filter.trim().toLowerCase();
        if (!q) return true;
        return (
            String(r.name || "").toLowerCase().includes(q) ||
            String(r.role || "").toLowerCase().includes(q) ||
            String(r.message || "").toLowerCase().includes(q)
        );
    });

    const toFormData = (f) => {
        const fd = new FormData();
        fd.append("name", f.name ?? "");
        fd.append("role", f.role ?? roleNameFromMe);
        fd.append("date", f.date ?? "");
        fd.append("message", f.message ?? "");
        if (f.file) fd.append("image", f.file);
        return fd;
    };

    const createItem = async (f) => {
        setSaving(true); setBanner({ type: "", text: "" });
        try {
            const { data, status } = await uploadFile(toFormData(f), "/api/testimonial", token);
            if (status === 200 && !data?.error) {
                setBanner({ type: "success", text: data?.message || "Testimonial created" });
                setModalOpen(false);
                await fetchList();
            } else {
                setBanner({ type: "danger", text: data?.message || "Failed to create testimonial" });
            }
        } catch {
            setBanner({ type: "danger", text: "Network error while creating testimonial" });
        } finally { setSaving(false); }
    };

    const saveEdit = async (id, f) => {
        setSaving(true); setBanner({ type: "", text: "" });
        try {
            const { data, status } = await uploadFile(toFormData(f), `/api/testimonial/update/${id}`, token);
            if (status === 200 && !data?.error) {
                setBanner({ type: "success", text: data?.message || "Testimonial updated" });
                setEditing(null);
                await fetchList();
            } else {
                setBanner({ type: "danger", text: data?.message || "Failed to update testimonial" });
            }
        } catch {
            setBanner({ type: "danger", text: "Network error while updating testimonial" });
        } finally { setSaving(false); }
    };

    const deleteItem = async (id) => {
        if (!window.confirm("Delete this testimonial?")) return;
        setBanner({ type: "", text: "" });
        try {
            const { data, status } = await deleteRequest(`/api/testimonial/${id}`, token);
            if (status === 200 && !data?.error) {
                setBanner({ type: "success", text: data?.message || "Testimonial deleted" });
                setRows((p) => p.filter((x) => String(x.id) !== String(id)));
            } else {
                setBanner({ type: "danger", text: data?.message || "Failed to delete testimonial" });
            }
        } catch {
            setBanner({ type: "danger", text: "Network error while deleting testimonial" });
        }
    };

    if (me === undefined) {
        return (<div className="d-flex align-items-center justify-content-center vh-100"><div className="text-center"><div className="spinner-border text-secondary" /><div className="text-muted small mt-2">Loading session…</div></div></div>);
    }

    // Any signed-in user can access (per your requirement)

    return (
        <div className="p-4 p-md-5">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-3 text-white d-flex align-items-center justify-content-center gradient-secondary" style={{ width: 48, height: 48 }}>
                        <MessageSquare size={24} />
                    </div>
                    <div>
                        <h1 className="fs-3 fw-bold mb-0">Testimonials</h1>
                        <div className="text-muted small">Create, edit and manage testimonials</div>
                    </div>
                </div>
                <button className="btn gradient-secondary text-white rounded-3 fw-semibold d-flex align-items-center gap-2 shadow" onClick={() => { setModalOpen(true); setEditing(null); }}>
                    <Plus size={18} /> New Testimonial
                </button>
            </div>

            {banner.text && <div className={`alert alert-${banner.type} rounded-3 py-2 px-3`}>{banner.text}</div>}

            <div className="bg-white rounded-3 border p-3 mb-3">
                <div className="row g-2 align-items-center">
                    <div className="col-md-6">
                        <div className="position-relative">
                            <Search className="position-absolute top-50 translate-middle-y text-muted" style={{ left: 12 }} size={18} />
                            <input className="form-control ps-5" placeholder="Search by name, role or message…" value={filter} onChange={(e) => setFilter(e.target.value)} />
                        </div>
                    </div>
                    <div className="col-md-6 text-end">
                        <span className="text-muted small">{filtered.length} items</span>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-3 border overflow-auto">
                <table className="table align-middle mb-0">
                    <thead className="table-light">
                        <tr>
                            <th style={{ width: 56 }}>Image</th>
                            <th>Name</th>
                            <th style={{ width: 140 }}>Role</th>
                            <th style={{ width: 140 }}>Date</th>
                            <th>Message</th>
                            <th style={{ width: 220 }} className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loadingList ? (
                            <tr><td colSpan={6} className="text-center py-5"><div className="spinner-border text-secondary" /><div className="text-muted small mt-2">Loading testimonials…</div></td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={6} className="text-center text-muted py-5">No testimonials found</td></tr>
                        ) : (
                            filtered.map((r, i) => {
                                const imgSrc = r.image || r.image_url || "";
                                const msg = String(r.message || "");
                                const short = msg.length > 90 ? msg.slice(0, 90) + "…" : msg;
                                return (
                                    <tr key={r.id || i}>
                                        <td>
                                            {imgSrc ? (
                                                <img src={imgSrc} alt="" className="rounded" style={{ width: 40, height: 40, objectFit: "cover" }} />
                                            ) : (
                                                <div className="rounded bg-light d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                                                    <ImageIcon size={16} />
                                                </div>
                                            )}
                                        </td>
                                        <td className="fw-semibold">{r.name || "—"}</td>
                                        <td>{r.role || "—"}</td>
                                        <td>{r.date || "—"}</td>
                                        <td className="text-muted small">{short}</td>
                                        <td className="text-end">
                                            <div className="d-inline-flex gap-2">
                                                <button
                                                    className="btn btn-outline-primary btn-sm rounded-3 d-inline-flex align-items-center gap-2"
                                                    onClick={async () => {
                                                        try {
                                                            const v = await loadTestimonial(r.id, token); // ← helper
                                                            const item = v || r;
                                                            setEditing({
                                                                id: r.id,
                                                                name: item.name ?? "",
                                                                role: item.role ?? roleNameFromMe,
                                                                date: item.date ?? "",
                                                                message: item.message ?? "",
                                                                file: null,
                                                                imagePreview: item.image || item.image_url || "",
                                                            });
                                                        } catch {
                                                            setEditing({
                                                                id: r.id,
                                                                name: r.name ?? "",
                                                                role: r.role ?? roleNameFromMe,
                                                                date: r.date ?? "",
                                                                message: r.message ?? "",
                                                                file: null,
                                                                imagePreview: r.image || r.image_url || "",
                                                            });
                                                        }
                                                    }}
                                                    title="Edit testimonial"
                                                >
                                                    <Edit size={16} /> Edit
                                                </button>
                                                <button className="btn btn-outline-danger btn-sm rounded-3 d-inline-flex align-items-center gap-2" onClick={() => deleteItem(r.id)} title="Delete testimonial">
                                                    <Trash2 size={16} /> Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <TmModal
                open={modalOpen && !editing}
                onClose={() => setModalOpen(false)}
                onSave={createItem}
                saving={saving}
                isEditing={false}
                fixedRole={roleNameFromMe}
            />

            <TmModal
                open={!!editing}
                onClose={() => setEditing(null)}
                onSave={(f) => saveEdit(editing.id, f)}
                saving={saving}
                initial={editing || undefined}
                isEditing
                fixedRole={roleNameFromMe}
            />
        </div>
    );
}
